<template>
  <SpecialOfferCarouselShimmer v-if="isLoading || isExperimentLoading" />
  <div v-else-if="shouldShowCarousel">
    <div class="flex w-full items-center justify-between">
      <h3 class="text-text-neutral-primary text-base font-medium">
        {{ t('specialOffer.title') }}
      </h3>
      <button
        v-if="!isSingleOffer"
        type="button"
        class="flex items-center space-x-0.5"
        @click="openSpecialOffersPage"
      >
        <span class="text-text-positive-primary text-sm font-medium">
          {{ $t('general.all') }}
        </span>
        <ChevronIcon class="text-text-positive-primary h-4 w-4" />
      </button>
    </div>
    <div class="relative pt-4">
      <FreeCarousel
        class="-mx-4 px-4"
        :carousel-config="{ pagination: { dynamicBullets: false } }"
      >
        <FreeCarouselItem
          v-if="isFreedomPrimeOfferEnabled"
          key="freedom-prime-offer"
        >
          <FreedomPrimeCard
            source="main"
            :is-single-offer="isSingleOffer"
            is-centered
          />
        </FreeCarouselItem>
        <FreeCarouselItem v-for="(item, idx) in specials" :key="item.id">
          <SpecialOfferCard
            :id="item.id"
            :title="item.title"
            :description="item.subtitle"
            :img="item.image_url"
            :price="item.price"
            :tags="item.tags"
            :is-single-offer="isSingleOffer"
            is-centered
            :banner-type="getBannerType(item.type)"
            source="main"
          />
        </FreeCarouselItem>
      </FreeCarousel>
      <div
        class="special-offer-bullets absolute inset-x-0 z-[5] flex justify-center"
      >
        <div class="swiper-pagination"></div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { computed, watch } from 'vue';
  import { useI18n } from 'vue-i18n';
  import {
    FreeCarousel,
    FreeCarouselItem,
    amplitudeExperiment,
  } from 'freedom-shared';
  import { useRouter } from 'vue-router';

  import { specialOffersModel } from '@/features/special-offers';

  import { useSpecialOffers, BannerType } from '@/entities/special-offers';

  import { amplitudeEventsLib, analyticsEventsLib } from '@/shared/lib';

  import ChevronIcon from '@/assets/images/chevron-arrow-16x16.svg?component';

  import FreedomPrimeCard from './FreedomPrimeCard.vue';
  import SpecialOfferCard from './SpecialOfferCard.vue';
  import SpecialOfferCarouselShimmer from './SpecialOfferCarouselShimmer.vue';

  const { t } = useI18n();
  const router = useRouter();

  const specialOffersStore = specialOffersModel.useSpecialOffers();
  const depCityCode = specialOffersStore.origCityCode.value;
  const { specials, isLoading } = useSpecialOffers({ depCityCode });

  /**
   * A/B test: show the hardcoded Freedom Lifestyle offer as the first carousel
   * item for users bucketed into the "on" variant.
   */
  const { isVariant: isFreedomPrimeOfferEnabled, isExperimentLoading } =
    amplitudeExperiment.lib.checkExperimentVariant(
      amplitudeExperiment.config.flags.SPECIAL_OFFER_FRIENDS,
      'offer_on',
    );

  const totalItemsCount = computed(
    () => specials.value.length + (isFreedomPrimeOfferEnabled.value ? 1 : 0),
  );
  const isSingleOffer = computed(() => totalItemsCount.value === 1);
  const shouldShowCarousel = computed(() => totalItemsCount.value > 0);

  const openSpecialOffersPage = () => {
    amplitudeEventsLib.allSpecialOffersPageOpened();

    router.push({
      name: 'SpecialOffersPage',
    });
  };

  const getBannerType = (type: string) => {
    if (type === 'EVENT') {
      return BannerType.EVENTS;
    }
    return BannerType.FLIGHTS;
  };

  watch(
    isFreedomPrimeOfferEnabled,
    isEnabled => {
      if (isEnabled) {
        analyticsEventsLib.specialOfferFriendsShown({ source: 'main' });
      }
    },
    { immediate: true },
  );
</script>

<style scoped>
  .special-offer-bullets :deep(.swiper-pagination-bullet) {
    background: white;
    opacity: 0.2;
    width: 6px;
    height: 6px;
    transform: scale(1);
    margin-inline: 3px;
    transition: all 0.3s ease;
  }

  .special-offer-bullets :deep(.swiper-pagination-bullet-active) {
    @apply bg-background-positive-primary;
    opacity: 1;
    width: 12px;
    border-radius: 4px;
    transition: all 0.3s ease;
  }
</style>
